<template><div></div></template>

<script setup lang="ts">
import { watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Shepherd from 'shepherd.js';
import { offset } from '@floating-ui/dom';
import { isNonNullable, isNullable } from '@/utils/nullability-helpers';
import { st } from '@/i18n/localized-string';
import type { WalkthroughStepElement, WalkthroughStep } from '@shared-types/frontend-scenario-definition';
import { isWalkthroughEvent } from '@/types/scenario-definition';
import { useWalkthrough } from '@/composables/useWalkthrough';
import { useRoute } from 'vue-router';

const { walkthroughEvent, onWalkthroughComplete, onWalkthroughCancel } = useWalkthrough();

const { t } = useI18n();

const route = useRoute();

const tour = new Shepherd.Tour({
    useModalOverlay: true,
    confirmCancel: false,
});

/**
  Start: This is for hiding the tour when the route changes   
**/

const routeHandler = () => {
    if (tour) tour.hide();
};

watch(
    () => route.fullPath,
    () => {
        routeHandler();
    },
);

/**
  End: This is for hiding the tour when the route changes   
**/

watch(
    walkthroughEvent,
    (e, _, onCleanup) => {
        if (isNullable(e) || !isWalkthroughEvent(e)) {
            return;
        }

        // Unfortunately, shepherd only exposes a class for Step
        const steps = e.steps.map((step, i) => {
            const padding = step.padding ?? 15;
            return new Shepherd.Step(tour, {
                id: step.id,
                title: isNonNullable(step.title) ? t(st(step.title)) : undefined,
                text: t(st(step.text)),
                classes: 'custom-footer-step',
                modalOverlayOpeningPadding: padding,
                modalOverlayOpeningRadius: 8,
                floatingUIOptions: {
                    middleware: [offset({ mainAxis: padding + 12 })],
                },
                attachTo: {
                    element: () => extractSelector(step.element),
                    on: step.position ?? 'bottom',
                },
                beforeShowPromise: () => {
                    return new Promise<void>((resolve) => {
                        const selector = extractSelector(step.element);
                        let timeCount = 0;
                        const intervalDuration = 100;
                        const maxTime = 2000;
                        function checkElementLoaded() {
                            const element = document.querySelector(selector);
                            if (isNonNullable(element)) {
                                resolve();
                            } else {
                                timeCount += intervalDuration;
                                if (timeCount < maxTime) {
                                    setTimeout(checkElementLoaded, intervalDuration);
                                } else {
                                    tour.next();
                                }
                            }
                        }
                        checkElementLoaded();
                    });
                },
                when: {
                    show: () => {
                        // We'll inject custom HTML into the footer here
                        addCustomFooterContent(e.steps, i);
                    },
                },
                canClickTarget: false,
                buttons: [
                    {
                        text: t('skip'),
                        action: tour.cancel,
                        classes: 'btn btn-dark opacity-50',
                    },
                    {
                        text: t(i === e.steps.length - 1 ? 'finish' : 'continue'),
                        classes: 'btn btn-primary',
                        action: tour.next,
                    },
                ],
            });
        });

        tour.steps = steps;

        tour.on('complete', () => {
            onWalkthroughComplete();
        });

        tour.on('cancel', () => {
            onWalkthroughCancel();
        });

        setTimeout(() => tour.start(), 100);

        onCleanup(() => {
            // TODO: remove second parameter once https://github.com/shipshapecode/shepherd/issues/2977 is fixed
            tour.off('complete', undefined as any);
        });
    },
    { immediate: true },
);

function addCustomFooterContent(steps: WalkthroughStep[], currentStepIndex: number) {
    // Get the currently visible step
    const stepEls = document.querySelectorAll('.custom-footer-step .shepherd-footer');

    stepEls.forEach((stepEl) => {
        // Create a new custom HTML block
        const customDiv = document.createElement('div');
        customDiv.innerHTML = `
            ${currentStepIndex + 1}/${steps.length}
        `;
        customDiv.classList.add('custom-footer-step-index');
        // Insert it before the first button
        const firstButton = stepEl.querySelector('button');
        if (firstButton) {
            stepEl.insertBefore(customDiv, firstButton);
        } else {
            stepEl.appendChild(customDiv);
        }
    });
}

function extractSelector(element: WalkthroughStepElement) {
    if ('decisionName' in element) {
        return '#decision-' + element.decisionName;
    }
    return element.selector;
}
</script>

<style lang="scss">
// Copied from shepherd.js/dist/css/shepherd.css and modified
.shepherd-element {
    --tour-background-color: var(--#{$prefix}black);

    background: var(--tour-background-color);
    color: var(--#{$prefix}white);
    border-radius: 8px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
    max-width: 400px;
    opacity: 0;
    outline: none;
    transition:
        opacity 0.3s,
        visibility 0.3s;
    visibility: hidden;
    width: 100%;
    z-index: 9999;
    box-sizing: border-box;

    *,
    :after,
    :before {
        box-sizing: border-box;
    }

    &.shepherd-enabled {
        opacity: 1;
        visibility: visible;
    }

    .shepherd-arrow {
        height: 16px;
        position: absolute;
        width: 16px;
        z-index: -1;

        &:before {
            height: 16px;
            position: absolute;
            width: 16px;
            z-index: -1;
            background: var(--tour-background-color);
            content: '';
            transform: rotate(45deg);
        }
    }

    &.shepherd-centered > .shepherd-arrow {
        opacity: 0;
    }

    &[data-popper-reference-hidden]:not(.shepherd-centered) {
        opacity: 0;
        pointer-events: none;
        visibility: hidden;
    }

    &[data-popper-placement^='top'] > .shepherd-arrow {
        bottom: -8px;
    }

    &[data-popper-placement^='bottom'] > .shepherd-arrow {
        top: -8px;
    }

    &[data-popper-placement^='left'] > .shepherd-arrow {
        right: -8px;
    }

    &[data-popper-placement^='right'] > .shepherd-arrow {
        left: -8px;
    }

    .shepherd-content {
        outline: none;
        padding: 0;

        .shepherd-header {
            align-items: center;
            border-top-left-radius: 6px;
            border-top-right-radius: 6px;
            display: flex;
            justify-content: flex-end;
            line-height: 2em;
            padding: 1rem 1rem 0;

            .shepherd-title {
                display: flex;
                flex: 1 0 auto;
                font-size: 1rem;
                font-weight: 400;
                margin: 0;
                padding: 0;
            }

            .shepherd-cancel-icon {
                background: transparent;
                border: none;
                color: hsla(0, 0%, 50%, 0.75);
                cursor: pointer;
                font-size: 2em;
                font-weight: 400;
                margin: 0;
                padding: 0;
                transition: color 0.5s ease;

                &:hover {
                    color: rgba(0, 0, 0, 0.75);
                }
            }
        }

        .shepherd-text {
            font-size: 1rem;
            line-height: 1.3em;
            padding: 1rem;
        }

        .shepherd-footer {
            border-bottom-left-radius: 6px;
            border-bottom-right-radius: 6px;
            display: flex;
            gap: 0.5rem;
            align-items: center;
            justify-content: flex-end;
            padding: 0 1rem 1rem;
            margin-top: 0.25rem;

            .shepherd-button:last-child {
                margin-right: 0;
            }
        }
    }

    &.shepherd-has-title {
        &[data-popper-placement^='bottom'] > .shepherd-arrow:before {
            background-color: #e6e6e6;
        }

        .shepherd-content {
            .shepherd-cancel-icon {
                color: hsla(0, 0%, 50%, 0.75);

                &:hover {
                    color: rgba(0, 0, 0, 0.75);
                }
            }

            .shepherd-header {
                background-color: $gray-200;
                color: $gray-800;
                padding: 1rem;
            }
        }
    }
}

.shepherd-target-click-disabled.shepherd-enabled.shepherd-target {
    pointer-events: none;

    * {
        pointer-events: none;
    }
}

.shepherd-modal-overlay-container {
    height: 0;
    left: 0;
    opacity: 0;
    overflow: hidden;
    pointer-events: none;
    position: fixed;
    top: 0;
    transition:
        all 0.3s ease-out,
        height 0ms 0.3s,
        opacity 0.3s 0ms;
    width: 100vw;
    z-index: 9997;

    &.shepherd-modal-is-visible {
        height: 100vh;
        opacity: 0.5;
        transform: translateZ(0);
        transition:
            all 0.3s ease-out,
            height 0s 0s,
            opacity 0.3s 0s;

        path {
            pointer-events: all;
        }
    }
}

@include color-mode(dark) {
    .shepherd-element {
        background-color: $gray-600-dark;

        .shepherd-arrow:before {
            background: $gray-600-dark;
        }

        &.shepherd-has-title {
            &[data-popper-placement^='bottom'] > .shepherd-arrow:before {
                background-color: $gray-700-dark;
            }

            .shepherd-content .shepherd-header {
                color: $gray-300;
                background-color: $gray-700-dark;
            }
        }
    }

    .shepherd-modal-overlay-container.shepherd-modal-is-visible {
        opacity: 0.6;
    }
}
</style>

<style lang="scss">
.custom-footer-step-index {
    font-size: 0.75em;
    opacity: 0.5;
    margin-right: auto;
}
</style>
